import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, User, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CfELogo = "https://customer-assets.emergentagent.com/job_startup-central-3/artifacts/3g256hrq_CfE%20Logo.png";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, formData);
      
      // Use sessionStorage - clears when browser/tab closes
      sessionStorage.setItem('cfe_session_token', response.data.access_token);
      
      toast.success('Welcome to CfE Admin Dashboard!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error('Login failed', {
        description: error.response?.data?.detail || 'Invalid credentials'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F3F4F6] p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <img 
            src={CfELogo} 
            alt="CfE Logo" 
            className="h-32 mx-auto mb-6"
          />
          <h1 className="text-2xl font-bold text-[#231F20] font-['Bricolage_Grotesque']">
            Admin Login
          </h1>
          <p className="text-[#58595B] mt-2">
            Sign in to manage CfE data
          </p>
        </div>

        <Card className="shadow-lg">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label className="form-label">Username</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                    className="form-input pl-12"
                    placeholder="Enter username"
                    data-testid="admin-username-input"
                  />
                </div>
              </div>

              <div>
                <Label className="form-label">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="form-input pl-12 pr-12"
                    placeholder="Enter password"
                    data-testid="admin-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#075FA3] hover:bg-[#0077B6] text-white rounded-full py-6 font-semibold"
                data-testid="admin-submit-btn"
              >
                {loading ? 'Please wait...' : 'Sign In'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-[#58595B] mt-6">
          <a href="/" className="hover:text-[#075FA3]">← Back to website</a>
        </p>
      </motion.div>
    </div>
  );
}
